import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Save, FileDown, Send, CheckCircle, Paperclip, Hammer, ExternalLink, Plus, Trash2, ChevronDown, FileText, Loader2, Upload, Eye, AlertTriangle, History, X, Download, Search, ShieldCheck, RotateCcw, Mail, XCircle, Printer, MapPin, UserCircle } from 'lucide-react'
import api from '../../../core/services/api'
import { Toast, ConfirmModal } from '../../../components/Toast'
import { StatusBadge } from '../../../components/offerte/StatusBadge'
import { PermissionGate } from '../../../components/PermissionGate'
import { OwnershipBadge } from '../../../components/OwnershipBadge'
import { TransferOwnershipModal } from '../../../components/TransferOwnershipModal'
import { RequestApprovalModal } from '../../../components/RequestApprovalModal'
import { useCanEdit } from '../../../hooks/usePermission'
import { useAuthStore } from '../../../core/stores/authStore'
import { ClienteSelector } from '../../../components/offerte/ClienteSelector'
import { CostiSection } from '../../../components/offerte/CostiSection'
import { ValoriSection } from '../../../components/offerte/ValoriSection'
import { CanoniSection } from '../../../components/offerte/CanoniSection'
import { ProfitChart } from '../../../components/offerte/ProfitChart'
import { AllegatiModal } from '../../../components/allegati/AllegatiModal'
import FotoSection from '../../../components/offerte/FotoSection'
import ClienteStatsCard from '../../../components/ClienteStatsCard'
import { useDesktopAgent } from '../../../hooks/useDesktopAgent'
import InviaEmailModal from '../../../components/offerte/InviaEmailModal'
import {
  OffertaDetail,
  OffertaCreate,
  OffertaUpdate,
  VoceUpdate,
  CostoDirettoCreate,
  CostoMagazzinoCreate,
  VoceLiberaCreate,
  Cliente,
  ListinoItem,
  StatoOfferta,
  IndirizzoOption,
  TipoScaglione,
  ScaglioneCreate,
  OffertaRevisione,
  VerificaDocumentoResult,
  CanoneUpdate,
  CanoneLiberoCrea,
  FrequenzaUnita,
  ReferenteCliente,
} from '../../../types/offerte'
import { TemplateDocumento } from '../../../types/template'

// Tipo per foto offerta
interface Foto {
  id: string
  titolo: string
  filename: string
  file_size: number
  content_type: string
  ordine: number
  created_at: string
}

interface UserMinimal { id: string; username: string; first_name?: string; last_name?: string }

export default function OffertaDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isNew = id === 'nuova'
  const { user: authUser } = useAuthStore()

  const [offerta, setOfferta] = useState<OffertaDetail | null>(null)
  const canEditOwnership = useCanEdit('offerte', offerta?.owner_id)
  const { available: agentAvailable, openWithAgent } = useDesktopAgent()
  const [loading, setLoading] = useState(!isNew)
  const [utenti, setUtenti] = useState<UserMinimal[]>([])
  const [saving, setSaving] = useState(false)
  const [clienti, setClienti] = useState<Cliente[]>([])
  const [listino, setListino] = useState<ListinoItem[]>([])
  const [foto, setFoto] = useState<Foto[]>([])
  const [templates, setTemplates] = useState<TemplateDocumento[]>([])
  const [showTemplateMenu, setShowTemplateMenu] = useState(false)
  const [generatingDoc, setGeneratingDoc] = useState(false)

  // Form state per nuova offerta
  const [form, setForm] = useState<OffertaCreate>({
    titolo: '',
    descrizione: '',
    cliente_id: '',
    owner_id: '',
    indirizzo_consegna: '',
    citta_consegna: '',
    cap_consegna: '',
    provincia_consegna: '',
    note: '',
    validita_giorni: 30
  })

  // Indirizzi cliente per dropdown
  const [indirizziCliente, setIndirizziCliente] = useState<IndirizzoOption[]>([])
  const [loadingIndirizzi, setLoadingIndirizzi] = useState(false)
  // Referenti cliente per dropdown
  const [referentiCliente, setReferentiCliente] = useState<ReferenteCliente[]>([])

  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null)
  const [confirmStato, setConfirmStato] = useState<StatoOfferta | null>(null)
  const [showAllegati, setShowAllegati] = useState(false)
  const [showEmailModal, setShowEmailModal] = useState(false)

  // Commessa collegata
  const [commessaLink, setCommessaLink] = useState<{ id: string; numero_commessa: string } | null>(null)
  const [showCommessaModal, setShowCommessaModal] = useState(false)
  const [commessaForm, setCommessaForm] = useState({
    data_prevista_consegna: '',
    responsabile_id: ''
  })
  const [personaleList, setPersonaleList] = useState<{ id: string; nome: string; cognome: string }[]>([])
  const [creatingCommessa, setCreatingCommessa] = useState(false)

  // Scaglioni pagamento
  const [scaglioneForm, setScaglioneForm] = useState<ScaglioneCreate>({
    descrizione: '',
    tipo: 'percentuale',
    percentuale: undefined,
    importo: undefined,
  })
  const [showScaglioneForm, setShowScaglioneForm] = useState(false)

  // ONLYOFFICE editor per documento offerta
  const [showOnlyoffice, setShowOnlyoffice] = useState(false)
  const [onlyofficeLoading, setOnlyofficeLoading] = useState(false)
  const onlyofficeRef = useRef<HTMLDivElement>(null)
  const onlyofficeEditorRef = useRef<any>(null)

  // Revisioni
  const [revisioni, setRevisioni] = useState<OffertaRevisione[]>([])
  const [showRevisioni, setShowRevisioni] = useState(false)
  const [revisioneNote, setRevisioneNote] = useState('')
  const [creatingRevisione, setCreatingRevisione] = useState(false)

  // Verifica documento
  const [showVerifica, setShowVerifica] = useState(false)
  const [verificaResult, setVerificaResult] = useState<VerificaDocumentoResult | null>(null)
  const [verificaLoading, setVerificaLoading] = useState(false)

  // Contratto AI
  const [generandoContratto, setGenerandoContratto] = useState(false)
  const [contrattoInfo, setContrattoInfo] = useState<{ has_contratto: boolean; allegato_id?: string; filename?: string } | null>(null)
  const [showContrattoEditor, setShowContrattoEditor] = useState(false)
  const [contrattoEditorLoading, setContrattoEditorLoading] = useState(false)
  const contrattoEditorRef = useRef<HTMLDivElement>(null)
  const contrattoEditorInstanceRef = useRef<any>(null)

  // Conferma offerta con picker gestore pagamento
  const [showConfermaModal, setShowConfermaModal] = useState(false)
  const [gestorePagamentoId, setGestorePagamentoId] = useState('')
  const [utentiAssegnabili, setUtentiAssegnabili] = useState<{ id: string; nome_completo: string }[]>([])
  const [loadingUtenti, setLoadingUtenti] = useState(false)
  const [confirmingConferma, setConfirmingConferma] = useState(false)

  // Notifica assegnazione commessa
  const [notificaUserId, setNotificaUserId] = useState('')

  // Invio forzato (stock insufficiente)
  const [showInvioForzato, setShowInvioForzato] = useState(false)
  const [showTransfer, setShowTransfer] = useState(false)
  const [showApprovalRequest, setShowApprovalRequest] = useState(false)
  const [invioPassword, setInvioPassword] = useState('')
  const [invioLoading, setInvioLoading] = useState(false)

  // Upload documento
  const uploadRef = useRef<HTMLInputElement>(null)

  // Edit in Word (File System Access API)
  const wordFileHandleRef = useRef<any>(null)
  const [wordEditMode, setWordEditMode] = useState(false)
  const [wordSaving, setWordSaving] = useState(false)
  const [wordUploading, setWordUploading] = useState(false)

  // Popover cambio sede/referente in header
  const [showSedeRefPopover, setShowSedeRefPopover] = useState(false)

  // Offerta integrativa
  const [showIntegrativaModal, setShowIntegrativaModal] = useState(false)
  const [integrativaForm, setIntegrativaForm] = useState({ titolo: '', descrizione: '' })
  const [creatingIntegrativa, setCreatingIntegrativa] = useState(false)

  useEffect(() => {
    loadData()
  }, [id])

  // Chiudi menu template cliccando fuori
  useEffect(() => {
    const handleClickOutside = () => setShowTemplateMenu(false)
    if (showTemplateMenu) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [showTemplateMenu])

  // Chiudi popover sede/referente cliccando fuori
  useEffect(() => {
    const handleClickOutside = () => setShowSedeRefPopover(false)
    if (showSedeRefPopover) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [showSedeRefPopover])

  const loadData = async () => {
    try {
      setLoading(true)
      const [clientiRes, listinoRes, utentiRes] = await Promise.all([
        api.get('/v1/anagrafiche/clienti'),
        api.get('/v1/anagrafiche/listino'),
        api.get('/v1/anagrafiche/utenti'),
      ])
      setClienti(clientiRes.data)
      setListino(listinoRes.data)
      setUtenti(utentiRes.data || [])

      // Default owner_id all'utente corrente (solo per nuova offerta)
      if (isNew && authUser?.user_id) {
        setForm(prev => ({ ...prev, owner_id: prev.owner_id || authUser.user_id }))
      }

      if (!isNew && id) {
        const [offertaRes, fotoRes, templatesRes] = await Promise.all([
          api.get(`/v1/offerte/${id}`),
          api.get(`/v1/offerte/${id}/foto`),
          api.get('/v1/template-documenti?tipo=offerta&attivo=true')
        ])
        setOfferta(offertaRes.data)
        setFoto(fotoRes.data || [])
        setTemplates(templatesRes.data || [])

        // Carica referenti e indirizzi del cliente
        if (offertaRes.data.cliente?.id) {
          loadReferentiCliente(offertaRes.data.cliente.id)
          loadIndirizziCliente(offertaRes.data.cliente.id)
        }

        // Carica commessa collegata se offerta confermata
        if (offertaRes.data.stato === 'confermata') {
          try {
            const commessaRes = await api.get(`/v1/commesse/per-offerta/${id}`)
            if (commessaRes.data) {
              setCommessaLink(commessaRes.data)
            }
          } catch { /* silenzioso */ }

          // Carica personale per il modale creazione commessa
          try {
            const persRes = await api.get('/v1/personale')
            setPersonaleList(persRes.data.map((p: any) => ({
              id: p.id, nome: p.nome, cognome: p.cognome
            })))
          } catch { /* silenzioso */ }
        }

        // Carica info contratto
        try {
          const contrattoRes = await api.get(`/v1/offerte/${id}/has-contratto`)
          setContrattoInfo(contrattoRes.data)
        } catch { /* silenzioso */ }
      }
    } catch (err) {
      console.error('Errore caricamento:', err)
      setToast({ message: 'Errore nel caricamento', type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const loadDataKeepScroll = async () => {
    const scrollY = window.scrollY
    await loadData()
    requestAnimationFrame(() => window.scrollTo(0, scrollY))
  }

  const loadIndirizziCliente = async (clienteId: string, autoSelect = false) => {
    if (!clienteId) {
      setIndirizziCliente([])
      return
    }
    try {
      setLoadingIndirizzi(true)
      const res = await api.get(`/v1/anagrafiche/clienti/${clienteId}/indirizzi`)
      setIndirizziCliente(res.data)
      // Auto-seleziona solo per nuova offerta: preferita > prima disponibile
      if (autoSelect && res.data.length > 0) {
        const preferita = res.data.find((i: IndirizzoOption) => i.is_preferita) || res.data[0]
        setForm(prev => ({
          ...prev,
          indirizzo_consegna: preferita.via || '',
          citta_consegna: preferita.citta || '',
          cap_consegna: preferita.cap || '',
          provincia_consegna: preferita.provincia || '',
        }))
      }
    } catch {
      setIndirizziCliente([])
    } finally {
      setLoadingIndirizzi(false)
    }
  }

  const handleSelectIndirizzo = (idx: number) => {
    if (idx === -1) {
      // Personalizzato
      setForm(prev => ({
        ...prev,
        indirizzo_consegna: '',
        citta_consegna: '',
        cap_consegna: '',
        provincia_consegna: '',
      }))
      return
    }
    const ind = indirizziCliente[idx]
    if (ind) {
      setForm(prev => ({
        ...prev,
        indirizzo_consegna: ind.via || '',
        citta_consegna: ind.citta || '',
        cap_consegna: ind.cap || '',
        provincia_consegna: ind.provincia || '',
      }))
    }
  }

  const loadReferentiCliente = async (clienteId: string) => {
    if (!clienteId) { setReferentiCliente([]); return }
    try {
      const res = await api.get(`/v1/anagrafiche/clienti/${clienteId}/referenti`)
      setReferentiCliente(res.data)
    } catch {
      setReferentiCliente([])
    }
  }

  const handleClienteChange = async (clienteId: string) => {
    setForm(prev => ({ ...prev, cliente_id: clienteId, referente_id: undefined }))
    loadIndirizziCliente(clienteId, true)
    // Auto-seleziona referente preferito
    if (clienteId) {
      try {
        const res = await api.get(`/v1/anagrafiche/clienti/${clienteId}/referenti`)
        setReferentiCliente(res.data)
        const preferito = res.data.find((r: ReferenteCliente) => r.is_preferito)
        if (preferito) {
          setForm(prev => ({ ...prev, referente_id: preferito.id }))
        }
      } catch {
        setReferentiCliente([])
      }
    } else {
      setReferentiCliente([])
    }
  }

  const handleCreate = async () => {
    if (!form.titolo || !form.cliente_id) {
      setToast({ message: 'Compila titolo e cliente', type: 'warning' })
      return
    }

    try {
      setSaving(true)
      const res = await api.post('/v1/offerte', form)
      setToast({ message: 'Offerta creata con successo', type: 'success' })
      navigate(`/app/offerte/${res.data.id}`, { replace: true })
    } catch (err) {
      console.error('Errore creazione:', err)
      setToast({ message: 'Errore nella creazione', type: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const handleUpdate = async (updates: OffertaUpdate) => {
    if (!offerta) return

    try {
      setSaving(true)
      const res = await api.put(`/v1/offerte/${offerta.id}`, updates)
      setOfferta(res.data)
      setToast({ message: 'Offerta aggiornata', type: 'success' })
    } catch (err) {
      console.error('Errore aggiornamento:', err)
      setToast({ message: 'Errore nell\'aggiornamento', type: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const handleAddCosto = async (data: CostoDirettoCreate) => {
    if (!offerta) return

    try {
      await api.post(`/v1/offerte/${offerta.id}/costi-diretti`, data)
      loadDataKeepScroll()
      setToast({ message: 'Costo aggiunto', type: 'success' })
    } catch (err: any) {
      console.error('Errore:', err)
      const detail = err?.response?.data?.detail || 'Errore nell\'aggiunta del costo'
      setToast({ message: detail, type: 'error' })
    }
  }

  const handleAddCostoMagazzino = async (data: CostoMagazzinoCreate) => {
    if (!offerta) return
    const res = await api.post(`/v1/offerte/${offerta.id}/costi-diretti/magazzino`, data)
    loadDataKeepScroll()
    if (res.data.warning_scorte) {
      setToast({ message: 'Articolo aggiunto — scorte insufficienti', type: 'warning' })
    } else {
      setToast({ message: 'Articolo da magazzino aggiunto', type: 'success' })
    }
    return res.data
  }

  const handleAddVoceLibera = async (data: VoceLiberaCreate) => {
    if (!offerta) return

    try {
      await api.post(`/v1/offerte/${offerta.id}/voci/libera`, data)
      loadDataKeepScroll()
      setToast({ message: 'Voce libera aggiunta', type: 'success' })
    } catch (err) {
      console.error('Errore:', err)
      setToast({ message: 'Errore nell\'aggiunta della voce', type: 'error' })
    }
  }

  const handleDeleteCosto = async (costoId: string) => {
    if (!offerta) return

    try {
      await api.delete(`/v1/offerte/${offerta.id}/costi-diretti/${costoId}`)
      loadDataKeepScroll()
      setToast({ message: 'Costo eliminato', type: 'success' })
    } catch (err) {
      console.error('Errore:', err)
      setToast({ message: 'Errore nell\'eliminazione', type: 'error' })
    }
  }

  const handleAddVoce = async (listinoId: string, quantita: number) => {
    if (!offerta) return

    try {
      await api.post(`/v1/offerte/${offerta.id}/voci`, { listino_id: listinoId, quantita })
      loadDataKeepScroll()
      setToast({ message: 'Voce aggiunta', type: 'success' })
    } catch (err) {
      console.error('Errore:', err)
      setToast({ message: 'Errore nell\'aggiunta della voce', type: 'error' })
    }
  }

  const handleUpdateVoce = async (voceId: string, updates: VoceUpdate) => {
    if (!offerta) return

    try {
      await api.put(`/v1/offerte/${offerta.id}/voci/${voceId}`, updates)
      loadDataKeepScroll()
    } catch (err) {
      console.error('Errore:', err)
      setToast({ message: 'Errore nell\'aggiornamento', type: 'error' })
    }
  }

  const handleUpdateNota = async (voceId: string, descrizione: string) => {
    if (!offerta) return

    try {
      await api.put(`/v1/offerte/${offerta.id}/voci/${voceId}`, { descrizione })
      loadDataKeepScroll()
    } catch (err) {
      console.error('Errore:', err)
      setToast({ message: 'Errore nell\'aggiornamento', type: 'error' })
    }
  }

  const handleAddNota = async (descrizione: string) => {
    if (!offerta) return

    try {
      await api.post(`/v1/offerte/${offerta.id}/voci/nota`, { descrizione })
      loadDataKeepScroll()
      setToast({ message: 'Nota aggiunta', type: 'success' })
    } catch (err) {
      console.error('Errore:', err)
      setToast({ message: 'Errore nell\'aggiunta della nota', type: 'error' })
    }
  }

  const handleReorderVoci = async (voceIds: string[]) => {
    if (!offerta) return

    try {
      const res = await api.put(`/v1/offerte/${offerta.id}/voci/riordina`, { voce_ids: voceIds })
      setOfferta(res.data)
    } catch (err) {
      console.error('Errore:', err)
      setToast({ message: 'Errore nel riordinamento', type: 'error' })
    }
  }

  const handleScontoFinaleChange = async (sconto: number) => {
    await handleUpdate({ sconto_finale_percentuale: sconto })
  }

  const handleDeleteVoce = async (voceId: string) => {
    if (!offerta) return

    try {
      await api.delete(`/v1/offerte/${offerta.id}/voci/${voceId}`)
      loadDataKeepScroll()
      setToast({ message: 'Voce eliminata', type: 'success' })
    } catch (err) {
      console.error('Errore:', err)
      setToast({ message: 'Errore nell\'eliminazione', type: 'error' })
    }
  }

  // ============ CANONI RICORSIVI ============

  const handleAddCanone = async (listinoId: string, quantita: number, frequenzaValore: number, frequenzaUnita: FrequenzaUnita, giorniPreavviso: number) => {
    if (!offerta) return
    try {
      await api.post(`/v1/offerte/${offerta.id}/canoni`, {
        listino_id: listinoId, quantita,
        frequenza_valore: frequenzaValore,
        frequenza_unita: frequenzaUnita,
        giorni_preavviso: giorniPreavviso,
      })
      loadDataKeepScroll()
      setToast({ message: 'Canone aggiunto', type: 'success' })
    } catch (err) {
      console.error('Errore:', err)
      setToast({ message: "Errore nell'aggiunta del canone", type: 'error' })
    }
  }

  const handleAddCanoneLibero = async (data: CanoneLiberoCrea) => {
    if (!offerta) return
    try {
      await api.post(`/v1/offerte/${offerta.id}/canoni/libero`, data)
      loadDataKeepScroll()
      setToast({ message: 'Canone libero aggiunto', type: 'success' })
    } catch (err) {
      console.error('Errore:', err)
      setToast({ message: "Errore nell'aggiunta del canone", type: 'error' })
    }
  }

  const handleAddCanoneNota = async (descrizione: string) => {
    if (!offerta) return
    try {
      await api.post(`/v1/offerte/${offerta.id}/canoni/nota`, { descrizione })
      loadDataKeepScroll()
      setToast({ message: 'Nota aggiunta', type: 'success' })
    } catch (err) {
      console.error('Errore:', err)
      setToast({ message: "Errore nell'aggiunta della nota", type: 'error' })
    }
  }

  const handleUpdateCanone = async (canoneId: string, updates: CanoneUpdate) => {
    if (!offerta) return
    try {
      await api.put(`/v1/offerte/${offerta.id}/canoni/${canoneId}`, updates)
      loadDataKeepScroll()
    } catch (err) {
      console.error('Errore:', err)
      setToast({ message: "Errore nell'aggiornamento", type: 'error' })
    }
  }

  const handleUpdateCanoneNota = async (canoneId: string, descrizione: string) => {
    if (!offerta) return
    try {
      await api.put(`/v1/offerte/${offerta.id}/canoni/${canoneId}`, { descrizione })
      loadDataKeepScroll()
    } catch (err) {
      console.error('Errore:', err)
      setToast({ message: "Errore nell'aggiornamento", type: 'error' })
    }
  }

  const handleDeleteCanone = async (canoneId: string) => {
    if (!offerta) return
    try {
      await api.delete(`/v1/offerte/${offerta.id}/canoni/${canoneId}`)
      loadDataKeepScroll()
      setToast({ message: 'Canone eliminato', type: 'success' })
    } catch (err) {
      console.error('Errore:', err)
      setToast({ message: "Errore nell'eliminazione", type: 'error' })
    }
  }

  const handleReorderCanoni = async (canoneIds: string[]) => {
    if (!offerta) return
    try {
      const res = await api.put(`/v1/offerte/${offerta.id}/canoni/riordina`, { canone_ids: canoneIds })
      setOfferta(res.data)
    } catch (err) {
      console.error('Errore:', err)
      setToast({ message: 'Errore nel riordinamento', type: 'error' })
    }
  }

  const loadUtentiAssegnabili = async () => {
    setLoadingUtenti(true)
    try {
      // Carica personale amministrativo con utente collegato
      const res = await api.get('/v1/personale/amministrativi/con-utente')
      setUtentiAssegnabili(res.data.map((u: any) => ({ id: u.id, nome_completo: u.nome_completo })))
    } catch {
      // Fallback: carica utenti assegnabili generici
      try {
        const res = await api.get('/v1/anagrafiche/utenti-assegnabili?modulo=scadenziario&azione=edit')
        setUtentiAssegnabili(res.data.map((u: any) => ({ id: u.id, nome_completo: u.nome_completo })))
      } catch { /* silent */ }
    }
    setLoadingUtenti(false)
  }

  const handleChangeStato = async () => {
    if (!offerta || !confirmStato) return

    // Se si sta inviando e ci sono problemi di stock, mostra modale password
    if (confirmStato === 'in_attesa' && offerta.stock_status && offerta.stock_status !== 'ok') {
      setConfirmStato(null)
      setShowInvioForzato(true)
      return
    }

    try {
      if (confirmStato === 'in_attesa') {
        await api.put(`/v1/offerte/${offerta.id}/invia`, {})
        loadData()
        setToast({ message: 'Offerta inviata', type: 'success' })
      }
      setConfirmStato(null)
    } catch (err: any) {
      const detail = err?.response?.data?.detail
      setToast({ message: typeof detail === 'string' ? detail : 'Errore invio offerta', type: 'error' })
      setConfirmStato(null)
    }
  }

  const handleConfermaOfferta = async () => {
    if (!offerta) return
    setConfirmingConferma(true)
    try {
      await handleUpdate({
        stato: 'confermata' as StatoOfferta,
        gestore_pagamento_id: gestorePagamentoId || undefined,
      })
      setShowConfermaModal(false)
      setGestorePagamentoId('')
    } catch (err) {
      console.error('Errore conferma:', err)
    } finally {
      setConfirmingConferma(false)
    }
  }

  const handleInvioForzato = async () => {
    if (!offerta || !invioPassword) return
    setInvioLoading(true)
    try {
      await api.put(`/v1/offerte/${offerta.id}/invia`, { password: invioPassword })
      loadData()
      setShowInvioForzato(false)
      setInvioPassword('')
      setToast({ message: 'Offerta inviata con forzatura', type: 'success' })
    } catch (err: any) {
      const detail = err?.response?.data?.detail
      setToast({ message: typeof detail === 'string' ? detail : 'Password non corretta', type: 'error' })
    } finally {
      setInvioLoading(false)
    }
  }

  const handleDownloadPdf = async () => {
    if (!offerta) return

    setShowTemplateMenu(false)
    try {
      const res = await api.get(`/v1/offerte/${offerta.id}/pdf`, {
        responseType: 'blob'
      })

      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `offerta_${offerta.numero_offerta}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Errore download PDF:', err)
      setToast({ message: 'Errore nel download del PDF', type: 'error' })
    }
  }

  const handleCreateCommessa = async () => {
    if (!offerta || !commessaForm.responsabile_id || !commessaForm.data_prevista_consegna) {
      setToast({ message: 'Compila responsabile e data consegna', type: 'warning' })
      return
    }
    try {
      setCreatingCommessa(true)
      const res = await api.post('/v1/commesse', {
        offerta_id: offerta.id,
        data_prevista_consegna: commessaForm.data_prevista_consegna,
        responsabile_id: commessaForm.responsabile_id,
        notifica_user_id: notificaUserId || undefined,
      })
      setToast({ message: 'Commessa creata con successo', type: 'success' })
      setShowCommessaModal(false)
      navigate(`/app/commesse/${res.data.id}`)
    } catch {
      setToast({ message: 'Errore nella creazione della commessa', type: 'error' })
    } finally {
      setCreatingCommessa(false)
    }
  }

  const handleCreateIntegrativa = async () => {
    if (!offerta || !integrativaForm.titolo.trim()) {
      setToast({ message: 'Inserisci un titolo', type: 'warning' })
      return
    }
    try {
      setCreatingIntegrativa(true)
      const res = await api.post(`/v1/offerte/${offerta.id}/integrative`, integrativaForm)
      setToast({ message: `Integrativa ${res.data.numero_offerta} creata`, type: 'success' })
      setShowIntegrativaModal(false)
      setIntegrativaForm({ titolo: '', descrizione: '' })
      navigate(`/app/offerte/${res.data.id}`)
    } catch (err: any) {
      setToast({ message: err?.response?.data?.detail || 'Errore creazione integrativa', type: 'error' })
    } finally {
      setCreatingIntegrativa(false)
    }
  }

  const handleAddScaglione = async () => {
    if (!offerta || !scaglioneForm.descrizione) return

    const payload: ScaglioneCreate = {
      descrizione: scaglioneForm.descrizione,
      tipo: scaglioneForm.tipo,
    }
    if (scaglioneForm.tipo === 'percentuale') {
      payload.percentuale = scaglioneForm.percentuale
    } else {
      payload.importo = scaglioneForm.importo
    }

    try {
      await api.post(`/v1/offerte/${offerta.id}/scaglioni`, payload)
      loadDataKeepScroll()
      setScaglioneForm({ descrizione: '', tipo: 'percentuale', percentuale: undefined, importo: undefined })
      setShowScaglioneForm(false)
      setToast({ message: 'Scaglione aggiunto', type: 'success' })
    } catch (err: any) {
      const detail = err?.response?.data?.detail || 'Errore nell\'aggiunta dello scaglione'
      setToast({ message: detail, type: 'error' })
    }
  }

  const handleDeleteScaglione = async (scaglioneId: string) => {
    if (!offerta) return

    try {
      await api.delete(`/v1/offerte/${offerta.id}/scaglioni/${scaglioneId}`)
      loadDataKeepScroll()
      setToast({ message: 'Scaglione eliminato', type: 'success' })
    } catch {
      setToast({ message: 'Errore nell\'eliminazione', type: 'error' })
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value)
  }

  const handleFotoChange = async () => {
    if (!offerta) return
    try {
      const res = await api.get(`/v1/offerte/${offerta.id}/foto`)
      setFoto(res.data || [])
    } catch (err) {
      console.error('Errore caricamento foto:', err)
    }
  }

  const handleGeneraDocumento = async (templateId: string) => {
    if (!offerta) return

    try {
      setGeneratingDoc(true)
      setShowTemplateMenu(false)
      const res = await api.post(`/v1/offerte/${offerta.id}/genera-documento/${templateId}`)

      setToast({ message: `Documento generato: ${res.data.filename}`, type: 'success' })

      // Aggiorna offerta con has_documento
      setOfferta(prev => prev ? { ...prev, has_documento: true, documento_filename: res.data.filename, data_generazione_documento: new Date().toISOString() } : null)

      // Apri ONLYOFFICE
      openOnlyofficeEditor()
    } catch (err) {
      console.error('Errore generazione documento:', err)
      setToast({ message: 'Errore nella generazione del documento', type: 'error' })
    } finally {
      setGeneratingDoc(false)
    }
  }

  const handleDownloadDocumento = async () => {
    if (!offerta) return
    try {
      const res = await api.get(`/v1/offerte/${offerta.id}/documento`, { responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', offerta.documento_filename || `offerta_${offerta.numero_offerta}.docx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Errore download documento:', err)
      setToast({ message: 'Errore nel download del documento', type: 'error' })
    }
  }

  const handleUploadDocumento = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!offerta || !e.target.files?.[0]) return
    const file = e.target.files[0]

    if (!file.name.endsWith('.docx')) {
      setToast({ message: 'Solo file .docx accettati', type: 'warning' })
      return
    }

    try {
      const formData = new FormData()
      formData.append('file', file)
      const res = await api.post(`/v1/offerte/${offerta.id}/documento/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      setToast({ message: `Documento caricato: ${res.data.filename}`, type: 'success' })
      setOfferta(prev => prev ? { ...prev, has_documento: true, documento_filename: res.data.filename, data_generazione_documento: new Date().toISOString() } : null)
    } catch (err) {
      console.error('Errore upload documento:', err)
      setToast({ message: 'Errore nel caricamento del documento', type: 'error' })
    }
    // Reset file input
    if (uploadRef.current) uploadRef.current.value = ''
  }

  // === MODIFICA IN WORD (File System Access API + Directory Handle persistito) ===
  const WORD_DB = 'middlelab-fs'
  const WORD_STORE = 'handles'

  const getStoredDirHandle = useCallback(async (): Promise<any | null> => {
    return new Promise((resolve) => {
      const req = indexedDB.open(WORD_DB, 1)
      req.onupgradeneeded = () => req.result.createObjectStore(WORD_STORE)
      req.onsuccess = () => {
        const tx = req.result.transaction(WORD_STORE, 'readonly')
        const get = tx.objectStore(WORD_STORE).get('wordDir')
        get.onsuccess = () => resolve(get.result || null)
        get.onerror = () => resolve(null)
      }
      req.onerror = () => resolve(null)
    })
  }, [])

  const storeDirHandle = useCallback(async (handle: any) => {
    return new Promise<void>((resolve) => {
      const req = indexedDB.open(WORD_DB, 1)
      req.onupgradeneeded = () => req.result.createObjectStore(WORD_STORE)
      req.onsuccess = () => {
        const tx = req.result.transaction(WORD_STORE, 'readwrite')
        tx.objectStore(WORD_STORE).put(handle, 'wordDir')
        tx.oncomplete = () => resolve()
      }
    })
  }, [])

  const handleWordEdit = async () => {
    if (!offerta) return
    if (!('showDirectoryPicker' in window)) {
      handleDownloadDocumento()
      setToast({ message: 'Browser non supportato per modifica diretta. File scaricato.', type: 'warning' })
      return
    }

    try {
      setWordSaving(true)

      // 1. Scarica il blob dal server
      const res = await api.get(`/v1/offerte/${offerta.id}/documento`, { responseType: 'blob' })
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      })
      const filename = offerta.documento_filename || `offerta_${offerta.numero_offerta}.docx`

      // 2. Prova directory salvata in IndexedDB (zero dialoghi dalla seconda volta)
      let dirHandle = await getStoredDirHandle()
      if (dirHandle) {
        try {
          const perm = await dirHandle.requestPermission({ mode: 'readwrite' })
          if (perm !== 'granted') dirHandle = null
        } catch { dirHandle = null }
      }

      // 3. Se non c'è handle, chiedi cartella (una tantum)
      if (!dirHandle) {
        dirHandle = await (window as any).showDirectoryPicker({
          id: 'middlelab-word',
          mode: 'readwrite',
          startIn: 'documents',
        })
        await storeDirHandle(dirHandle)
      }

      // 4. Scrivi il file nella cartella (sovrascrive se esiste)
      const fileHandle = await dirHandle.getFileHandle(filename, { create: true })
      const writable = await fileHandle.createWritable()
      await writable.write(blob)
      await writable.close()

      // 5. Conserva handle per rilettura
      wordFileHandleRef.current = fileHandle
      setWordEditMode(true)
      setToast({ message: 'File pronto! Aprilo dalla cartella, modificalo in Word e torna qui.', type: 'success' })
    } catch (err: any) {
      if (err?.name === 'AbortError') { setWordSaving(false); return }
      console.error('Errore Word edit con directory:', err)

      // Fallback: se permessi negati o errore, chiedi dove salvare
      try {
        const res2 = await api.get(`/v1/offerte/${offerta.id}/documento`, { responseType: 'blob' })
        const blob2 = new Blob([res2.data], {
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        })
        const handle = await (window as any).showSaveFilePicker({
          suggestedName: offerta.documento_filename || `offerta_${offerta.numero_offerta}.docx`,
          types: [{ description: 'Word', accept: { 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'] } }],
        })
        const w = await handle.createWritable()
        await w.write(blob2)
        await w.close()
        wordFileHandleRef.current = handle
        setWordEditMode(true)
        setToast({ message: 'File salvato! Aprilo in Word, modificalo e torna qui.', type: 'success' })
      } catch (e2: any) {
        if (e2?.name === 'AbortError') { setWordSaving(false); return }
        setToast({ message: 'Errore nel salvataggio del documento', type: 'error' })
      }
    } finally {
      setWordSaving(false)
    }
  }

  const handleWordUpload = async () => {
    if (!offerta || !wordFileHandleRef.current) return
    try {
      setWordUploading(true)
      const file = await wordFileHandleRef.current.getFile()
      const formData = new FormData()
      formData.append('file', file, file.name)
      const res = await api.post(`/v1/offerte/${offerta.id}/documento/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      setToast({ message: `Documento aggiornato: ${res.data.filename}`, type: 'success' })
      setOfferta(prev => prev ? {
        ...prev, has_documento: true, documento_filename: res.data.filename,
        data_generazione_documento: new Date().toISOString(),
      } : null)
      wordFileHandleRef.current = null
      setWordEditMode(false)
    } catch (err) {
      console.error('Errore upload Word:', err)
      setToast({ message: 'Errore nel caricamento. Chiudi prima Word e riprova.', type: 'error' })
    } finally {
      setWordUploading(false)
    }
  }

  const handleWordCancel = () => {
    wordFileHandleRef.current = null
    setWordEditMode(false)
  }

  // ONLYOFFICE editor
  const loadOnlyofficeScript = useCallback((apiUrl: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      if ((window as any).DocsAPI) {
        resolve()
        return
      }
      const existing = document.querySelector(`script[src="${apiUrl}"]`)
      if (existing) {
        const check = setInterval(() => {
          if ((window as any).DocsAPI) { clearInterval(check); resolve() }
        }, 200)
        setTimeout(() => { clearInterval(check); reject(new Error('Timeout loading ONLYOFFICE')) }, 15000)
        return
      }
      const script = document.createElement('script')
      script.src = apiUrl
      script.onload = () => {
        const check = setInterval(() => {
          if ((window as any).DocsAPI) { clearInterval(check); resolve() }
        }, 200)
        setTimeout(() => { clearInterval(check); reject(new Error('Timeout loading ONLYOFFICE')) }, 15000)
      }
      script.onerror = () => reject(new Error('Errore caricamento ONLYOFFICE'))
      document.head.appendChild(script)
    })
  }, [])

  const openOnlyofficeEditor = useCallback(async () => {
    if (!offerta) return
    try {
      setOnlyofficeLoading(true)
      setShowOnlyoffice(true)

      const { data } = await api.get(`/v1/onlyoffice/offerta-config/${offerta.id}`)
      await loadOnlyofficeScript(data.apiUrl)

      // Aspetta che il container sia renderizzato
      await new Promise(r => setTimeout(r, 300))

      if (onlyofficeEditorRef.current) {
        onlyofficeEditorRef.current.destroyEditor()
        onlyofficeEditorRef.current = null
      }

      onlyofficeEditorRef.current = new (window as any).DocsAPI.DocEditor(
        'onlyoffice-offerta-container',
        data.config
      )
    } catch (err) {
      console.error('Errore apertura ONLYOFFICE:', err)
      setToast({ message: 'Errore apertura editor ONLYOFFICE', type: 'error' })
      setShowOnlyoffice(false)
    } finally {
      setOnlyofficeLoading(false)
    }
  }, [offerta, loadOnlyofficeScript])

  const closeOnlyofficeEditor = useCallback(() => {
    if (onlyofficeEditorRef.current) {
      onlyofficeEditorRef.current.destroyEditor()
      onlyofficeEditorRef.current = null
    }
    setShowOnlyoffice(false)
    // Ricarica offerta per aggiornare stato documento
    if (offerta) {
      api.get(`/v1/offerte/${offerta.id}`).then(res => setOfferta(res.data)).catch(() => {})
    }
  }, [offerta])

  // ============ CONTRATTO AI ============
  const handleGeneraContratto = async () => {
    if (!offerta) return
    try {
      setGenerandoContratto(true)
      const res = await api.post(`/v1/offerte/${offerta.id}/genera-contratto`)
      setToast({ message: 'Contratto generato!', type: 'success' })
      setContrattoInfo({
        has_contratto: true,
        allegato_id: res.data.allegato_id,
        filename: res.data.filename,
      })
      // Apri subito in ONLYOFFICE
      openContrattoEditor()
    } catch (err: any) {
      console.error('Errore generazione contratto:', err)
      const detail = err?.response?.data?.detail || 'Errore nella generazione del contratto'
      setToast({ message: detail, type: 'error' })
    } finally {
      setGenerandoContratto(false)
    }
  }

  const openContrattoEditor = useCallback(async () => {
    if (!offerta) return
    try {
      setContrattoEditorLoading(true)
      setShowContrattoEditor(true)

      const { data } = await api.get(`/v1/onlyoffice/contratto-config/${offerta.id}`)
      await loadOnlyofficeScript(data.apiUrl)

      await new Promise(r => setTimeout(r, 300))

      if (contrattoEditorInstanceRef.current) {
        contrattoEditorInstanceRef.current.destroyEditor()
        contrattoEditorInstanceRef.current = null
      }

      contrattoEditorInstanceRef.current = new (window as any).DocsAPI.DocEditor(
        'onlyoffice-contratto-container',
        data.config
      )
    } catch (err) {
      console.error('Errore apertura ONLYOFFICE contratto:', err)
      setToast({ message: 'Errore apertura editor contratto', type: 'error' })
      setShowContrattoEditor(false)
    } finally {
      setContrattoEditorLoading(false)
    }
  }, [offerta, loadOnlyofficeScript])

  const closeContrattoEditor = useCallback(() => {
    if (contrattoEditorInstanceRef.current) {
      contrattoEditorInstanceRef.current.destroyEditor()
      contrattoEditorInstanceRef.current = null
    }
    setShowContrattoEditor(false)
    // Ricarica info contratto
    if (offerta) {
      api.get(`/v1/offerte/${offerta.id}/has-contratto`).then(res => setContrattoInfo(res.data)).catch(() => {})
    }
  }, [offerta])

  const handleDownloadContratto = async () => {
    if (!offerta) return
    try {
      const res = await api.get(`/v1/offerte/${offerta.id}/contratto`, { responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      link.download = contrattoInfo?.filename || `contratto_${offerta.numero_offerta}.docx`
      link.click()
      window.URL.revokeObjectURL(url)
    } catch {
      setToast({ message: 'Errore download contratto', type: 'error' })
    }
  }

  // Revisioni
  const loadRevisioni = async () => {
    if (!offerta) return
    try {
      const res = await api.get(`/v1/offerte/${offerta.id}/revisioni`)
      setRevisioni(res.data)
    } catch (err) {
      console.error('Errore caricamento revisioni:', err)
    }
  }

  const handleCreaRevisione = async () => {
    if (!offerta) return
    try {
      setCreatingRevisione(true)
      await api.post(`/v1/offerte/${offerta.id}/revisioni`, null, { params: { note: revisioneNote || undefined } })
      setToast({ message: 'Revisione creata', type: 'success' })
      setRevisioneNote('')
      setOfferta(prev => prev ? { ...prev, numero_revisione: (prev.numero_revisione || 0) + 1 } : null)
      await loadRevisioni()
    } catch (err) {
      console.error('Errore creazione revisione:', err)
      setToast({ message: 'Errore nella creazione della revisione', type: 'error' })
    } finally {
      setCreatingRevisione(false)
    }
  }

  const handleDownloadRevisioneDoc = async (revId: string) => {
    if (!offerta) return
    try {
      const res = await api.get(`/v1/offerte/${offerta.id}/revisioni/${revId}/documento`, { responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `revisione_${revId}.docx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      setToast({ message: 'Errore download documento revisione', type: 'error' })
    }
  }

  // Verifica documento
  const handleVerificaDocumento = async () => {
    if (!offerta) return
    try {
      setVerificaLoading(true)
      setShowVerifica(true)
      const res = await api.get(`/v1/offerte/${offerta.id}/verifica-documento`)
      setVerificaResult(res.data)
    } catch (err) {
      console.error('Errore verifica documento:', err)
      setToast({ message: 'Errore nella verifica del documento', type: 'error' })
      setShowVerifica(false)
    } finally {
      setVerificaLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Caricamento...</div>
      </div>
    )
  }

  // Form nuova offerta
  if (isNew) {
    return (
      <div>
        {toast && (
          <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
        )}

        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/app/offerte')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900">Nuova Offerta</h1>
          </div>
          <button
            onClick={handleCreate}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            <Save className="w-5 h-5" />
            {saving ? 'Salvataggio...' : 'Crea Offerta'}
          </button>
        </div>

        {/* Form */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="grid grid-cols-2 gap-6">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Titolo *</label>
              <input
                type="text"
                value={form.titolo}
                onChange={(e) => setForm({ ...form, titolo: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="es. Manutenzione impianto"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cliente *</label>
              <ClienteSelector
                clienti={clienti}
                selectedId={form.cliente_id}
                onChange={handleClienteChange}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Referente</label>
              <select
                value={form.referente_id || ''}
                onChange={(e) => setForm({ ...form, referente_id: e.target.value || undefined })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
                disabled={!form.cliente_id || referentiCliente.length === 0}
              >
                <option value="">-- Nessun referente --</option>
                {referentiCliente.map(r => (
                  <option key={r.id} value={r.id}>
                    {r.nome} {r.cognome}{r.ruolo ? ` (${r.ruolo})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Responsabile offerta</label>
              <select
                value={form.owner_id || ''}
                onChange={(e) => setForm({ ...form, owner_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
              >
                <option value="">-- Seleziona --</option>
                {utenti.map(u => (
                  <option key={u.id} value={u.id}>
                    {[u.first_name, u.last_name].filter(Boolean).join(' ') || u.username}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Validità (giorni)</label>
              <input
                type="number"
                min="1"
                value={form.validita_giorni}
                onChange={(e) => setForm({ ...form, validita_giorni: parseInt(e.target.value) || 30 })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione</label>
              <textarea
                rows={3}
                value={form.descrizione}
                onChange={(e) => setForm({ ...form, descrizione: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              />
            </div>

            <div className="col-span-2">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Indirizzo Consegna</h3>

              {/* Dropdown indirizzi cliente */}
              {form.cliente_id && indirizziCliente.length > 0 && (
                <div className="mb-3">
                  <select
                    onChange={(e) => handleSelectIndirizzo(parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white text-sm"
                    defaultValue="0"
                  >
                    {indirizziCliente.map((ind, idx) => (
                      <option key={idx} value={idx}>{ind.label}</option>
                    ))}
                    <option value="-1">Indirizzo personalizzato</option>
                  </select>
                </div>
              )}
              {loadingIndirizzi && (
                <p className="text-sm text-gray-400 mb-2">Caricamento indirizzi...</p>
              )}

              <div className="grid grid-cols-1 gap-3">
                <input
                  type="text"
                  placeholder="Via/Indirizzo"
                  value={form.indirizzo_consegna}
                  onChange={(e) => setForm({ ...form, indirizzo_consegna: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <div className="grid grid-cols-4 gap-2">
                  <input
                    type="text"
                    placeholder="CAP"
                    maxLength={5}
                    value={form.cap_consegna}
                    onChange={(e) => setForm({ ...form, cap_consegna: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <input
                    type="text"
                    placeholder="Citta"
                    value={form.citta_consegna}
                    onChange={(e) => setForm({ ...form, citta_consegna: e.target.value })}
                    className="col-span-2 w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <input
                    type="text"
                    placeholder="Prov."
                    maxLength={2}
                    value={form.provincia_consegna}
                    onChange={(e) => setForm({ ...form, provincia_consegna: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
            </div>
          </div>

          <p className="mt-6 text-sm text-gray-500">
            * Dopo aver creato l'offerta potrai aggiungere costi e voci dal listino.
          </p>
        </div>
      </div>
    )
  }

  // Visualizzazione/modifica offerta esistente
  if (!offerta) {
    return <div className="text-center text-gray-500 py-8">Offerta non trovata</div>
  }

  const isEditable = (offerta.stato === 'in_corso' || offerta.stato === 'bozza') && canEditOwnership

  return (
    <div>
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      {/* Confirm invio (solo in_attesa) */}
      <ConfirmModal
        isOpen={confirmStato === 'in_attesa'}
        title="Invia Offerta"
        message="Sei sicuro di voler inviare l'offerta al cliente?"
        confirmText="Invia"
        onConfirm={handleChangeStato}
        onCancel={() => setConfirmStato(null)}
      />

      {/* Modale Conferma Offerta con picker gestore pagamento */}
      {showConfermaModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Conferma Offerta</h2>
            <p className="text-sm text-gray-500 mb-4">
              Il cliente ha confermato l'offerta <strong>{offerta.numero_offerta}</strong>.
              Seleziona chi gestirà il pagamento.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gestore Pagamento</label>
                {loadingUtenti ? (
                  <div className="text-sm text-gray-400">Caricamento utenti...</div>
                ) : (
                  <select
                    value={gestorePagamentoId}
                    onChange={e => setGestorePagamentoId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
                  >
                    <option value="">Nessun gestore (opzionale)</option>
                    {utentiAssegnabili.map(u => (
                      <option key={u.id} value={u.id}>{u.nome_completo}</option>
                    ))}
                  </select>
                )}
                <p className="text-xs text-gray-400 mt-1">
                  Riceverà una notifica con l'assegnazione della gestione economica
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => { setShowConfermaModal(false); setGestorePagamentoId('') }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Annulla
              </button>
              <button
                onClick={handleConfermaOfferta}
                disabled={confirmingConferma}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {confirmingConferma ? 'Conferma in corso...' : 'Conferma Offerta'}
              </button>
            </div>
          </div>
        </div>
      )}

      <AllegatiModal
        isOpen={showAllegati}
        onClose={() => setShowAllegati(false)}
        entityType="offerta"
        entityId={offerta.id}
        readonly={!isEditable}
      />

      {/* Modale Invia Email */}
      <InviaEmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        offertaId={offerta.id}
        offertaNumero={offerta.numero_offerta}
        clienteNome={offerta.cliente.ragione_sociale}
        referenteEmail={offerta.referente?.email}
        onSent={() => {
          setToast({ message: 'Email inviata con successo', type: 'success' })
        }}
      />

      {/* Modale Crea Commessa */}
      {showCommessaModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Crea Commessa</h2>
            <p className="text-sm text-gray-500 mb-4">
              Trasforma l'offerta <strong>{offerta.numero_offerta}</strong> in commessa esecutiva
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data Prevista Consegna *</label>
                <input
                  type="date"
                  value={commessaForm.data_prevista_consegna}
                  onChange={e => setCommessaForm(prev => ({ ...prev, data_prevista_consegna: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Responsabile Commessa *</label>
                <select
                  value={commessaForm.responsabile_id}
                  onChange={e => setCommessaForm(prev => ({ ...prev, responsabile_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
                >
                  <option value="">Seleziona responsabile...</option>
                  {personaleList.map(p => (
                    <option key={p.id} value={p.id}>{p.cognome} {p.nome}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notifica assegnazione a</label>
                <select
                  value={notificaUserId}
                  onChange={e => setNotificaUserId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
                >
                  <option value="">Nessuna notifica</option>
                  {utentiAssegnabili.map(u => (
                    <option key={u.id} value={u.id}>{u.nome_completo}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 mt-1">L'utente riceverà una notifica di assegnazione commessa</p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowCommessaModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Annulla
              </button>
              <button
                onClick={handleCreateCommessa}
                disabled={creatingCommessa}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
              >
                {creatingCommessa ? 'Creazione...' : 'Crea Commessa'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => offerta.parent_offerta_id ? navigate(`/app/offerte/${offerta.parent_offerta_id}`) : navigate('/app/offerte')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          title={offerta.parent_offerta_id ? 'Torna all\'offerta principale' : 'Torna alla lista'}
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
              {offerta.numero_offerta}
              {(offerta.numero_revisione || 0) > 0 && <span className="text-purple-600 ml-1">Rev.{offerta.numero_revisione}</span>}
            </span>
            {offerta.tipo_offerta === 'integrativa' && (
              <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-semibold rounded-full">
                INTEGRATIVA n.{offerta.numero_integrativa}
              </span>
            )}
            <button
              onClick={() => { setShowRevisioni(!showRevisioni); if (!showRevisioni) loadRevisioni() }}
              className="flex items-center gap-1.5 px-2 py-0.5 border border-purple-300 text-purple-700 rounded hover:bg-purple-50 text-xs font-medium"
            >
              <History className="w-3 h-3" />
              Rev. {offerta.numero_revisione || 0}
            </button>
            <StatusBadge stato={offerta.stato} />
            {offerta.stock_status && offerta.stock_status !== 'ok' && (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                offerta.stock_status === 'insufficiente' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
              }`}>
                <AlertTriangle className="w-3 h-3" />
                Scorte {offerta.stock_status === 'insufficiente' ? 'insufficienti' : 'parziali'}
              </span>
            )}
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mt-1">{offerta.titolo}</h1>
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-gray-500">{offerta.cliente.ragione_sociale}</p>
            {/* Referente badge */}
            {offerta.referente && (
              <span className="inline-flex items-center gap-1 text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded">
                <UserCircle className="w-3 h-3" />
                {offerta.referente.nome} {offerta.referente.cognome}
              </span>
            )}
            {/* Sede/indirizzo consegna badge */}
            {(offerta.indirizzo_consegna || offerta.citta_consegna) && (
              <span className="inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded">
                <MapPin className="w-3 h-3" />
                {[offerta.indirizzo_consegna, offerta.citta_consegna, offerta.provincia_consegna].filter(Boolean).join(', ')}
              </span>
            )}
            {/* Bottone Cambia sede/referente — sempre visibile per owner/admin */}
            {canEditOwnership && (
              <div className="relative">
                <button
                  onClick={(e) => { e.stopPropagation(); setShowSedeRefPopover(!showSedeRefPopover) }}
                  className="text-xs text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 px-1.5 py-0.5 rounded transition-colors"
                  title="Cambia sede consegna o referente"
                >
                  Cambia ▾
                </button>
                {showSedeRefPopover && (
                  <div className="absolute left-0 top-full mt-1 w-80 bg-white rounded-lg shadow-xl border border-gray-200 p-3 z-20" onClick={(e) => e.stopPropagation()}>
                    {/* Sede consegna */}
                    <div className="mb-3">
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Sede consegna</label>
                      <select
                        value={indirizziCliente.findIndex(i =>
                          i.via === offerta.indirizzo_consegna && i.citta === offerta.citta_consegna
                        )}
                        onChange={(e) => {
                          const idx = parseInt(e.target.value)
                          if (idx >= 0) {
                            const ind = indirizziCliente[idx]
                            handleUpdate({
                              indirizzo_consegna: ind.via || '',
                              citta_consegna: ind.citta || '',
                              cap_consegna: ind.cap || '',
                              provincia_consegna: ind.provincia || '',
                            })
                          }
                        }}
                        className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm bg-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                      >
                        <option value={-1}>-- Seleziona sede --</option>
                        {indirizziCliente.map((ind, idx) => (
                          <option key={idx} value={idx}>
                            {ind.is_preferita ? '★ ' : ''}{ind.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    {/* Referente */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Referente</label>
                      <select
                        value={offerta.referente?.id || ''}
                        onChange={(e) => {
                          handleUpdate({ referente_id: e.target.value || null })
                        }}
                        className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm bg-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                      >
                        <option value="">-- Nessun referente --</option>
                        {referentiCliente.map(r => (
                          <option key={r.id} value={r.id}>
                            {r.is_preferito ? '★ ' : ''}{r.nome} {r.cognome}{r.ruolo ? ` (${r.ruolo})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      onClick={() => setShowSedeRefPopover(false)}
                      className="mt-2 w-full text-center text-xs text-gray-400 hover:text-gray-600"
                    >
                      Chiudi
                    </button>
                  </div>
                )}
              </div>
            )}
            {offerta.has_documento && (
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                Doc: {offerta.documento_filename}
              </span>
            )}
          </div>
        </div>

        {/* Azioni — toolbar compatta */}
        <div className="flex items-center gap-3 flex-wrap">
          {/* Owner + Cedi */}
          <OwnershipBadge
            ownerId={offerta.owner_id}
            ownerName={offerta.owner_name}
            onTransfer={() => setShowTransfer(true)}
          />
          <TransferOwnershipModal
            isOpen={showTransfer}
            onClose={() => setShowTransfer(false)}
            entityType="offerta"
            entityId={offerta.id}
            currentOwnerId={offerta.owner_id}
            onTransferred={() => {
              setToast({ message: 'Proprietà trasferita', type: 'success' })
              loadData()
            }}
          />

          {/* Separatore */}
          <div className="h-6 w-px bg-gray-200" />

          {/* Workflow buttons — icon-only con tooltip */}
          <div className="flex items-center gap-1">
            {/* Richiedi approvazione (bozza/in_corso) */}
            {(offerta.stato === 'bozza' || offerta.stato === 'in_corso') && canEditOwnership && (
              <button
                onClick={() => setShowApprovalRequest(true)}
                className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                title="Richiedi approvazione interna"
              >
                <ShieldCheck className="w-4 h-4" />
              </button>
            )}
            {/* Invio al cliente (bozza/in_corso/approvata) */}
            {(offerta.stato === 'in_corso' || offerta.stato === 'bozza' || offerta.stato === 'approvata') && canEditOwnership && (
              <button
                onClick={() => setConfirmStato('in_attesa')}
                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="Invia al cliente"
              >
                <Send className="w-4 h-4" />
              </button>
            )}
            {/* Riporta in bozza (rifiutata/in_attesa) */}
            {(offerta.stato === 'rifiutata' || offerta.stato === 'in_attesa') && canEditOwnership && (
              <button
                onClick={async () => {
                  try {
                    await api.post(`/v1/workflow/offerte/${offerta.id}/riporta-bozza`, {})
                    setToast({ message: 'Offerta riportata in bozza', type: 'success' })
                    loadData()
                  } catch (err: any) {
                    setToast({ message: err?.response?.data?.detail || 'Errore', type: 'error' })
                  }
                }}
                className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                title="Riporta in bozza"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            )}
            {/* Rifiuta (in_attesa) */}
            {offerta.stato === 'in_attesa' && canEditOwnership && (
              <button
                onClick={async () => {
                  try {
                    await api.post(`/v1/workflow/offerte/${offerta.id}/rifiuta`, {})
                    setToast({ message: 'Offerta segnata come rifiutata', type: 'success' })
                    loadData()
                  } catch (err: any) {
                    setToast({ message: err?.response?.data?.detail || 'Errore', type: 'error' })
                  }
                }}
                className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                title="Segna come rifiutata dal cliente"
              >
                <XCircle className="w-4 h-4" />
              </button>
            )}
            {/* Conferma (in_attesa) */}
            {offerta.stato === 'in_attesa' && (
              <button
                onClick={() => { setShowConfermaModal(true); loadUtentiAssegnabili() }}
                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                title="Conferma offerta (accettata dal cliente)"
              >
                <CheckCircle className="w-4 h-4" />
              </button>
            )}
            {/* Email (has_documento) */}
            {offerta.has_documento && (
              <button
                onClick={() => setShowEmailModal(true)}
                className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                title="Invia email con offerta allegata"
              >
                <Mail className="w-4 h-4" />
              </button>
            )}
            {/* Allegati */}
            <button
              onClick={() => setShowAllegati(true)}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
              title="Gestisci allegati"
            >
              <Paperclip className="w-4 h-4" />
            </button>
          </div>
          <RequestApprovalModal
            isOpen={showApprovalRequest}
            onClose={() => setShowApprovalRequest(false)}
            entityType="offerta"
            entityId={offerta.id}
            onSubmitted={() => {
              setToast({ message: 'Offerta inviata per approvazione', type: 'success' })
              loadData()
            }}
          />

          {/* Separatore */}
          <div className="h-6 w-px bg-gray-200" />

          {/* Commessa / Integrativa — solo se confermata */}
          {offerta.stato === 'confermata' && offerta.tipo_offerta !== 'integrativa' && (
            <div className="flex items-center gap-1">
              {!commessaLink ? (
                <PermissionGate module="commesse" action="create">
                  <button
                    onClick={() => { setShowCommessaModal(true); loadUtentiAssegnabili() }}
                    className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                    title="Crea commessa"
                  >
                    <Hammer className="w-4 h-4" />
                  </button>
                </PermissionGate>
              ) : (
                <button
                  onClick={() => navigate(`/app/commesse/${commessaLink.id}`)}
                  className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                  title={`Vai a ${commessaLink.numero_commessa}`}
                >
                  <ExternalLink className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => setShowIntegrativaModal(true)}
                className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                title="Crea offerta integrativa"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          )}
          {offerta.stato === 'confermata' && commessaLink && offerta.tipo_offerta === 'integrativa' && (
            <button
              onClick={() => navigate(`/app/commesse/${commessaLink.id}`)}
              className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
              title={`Vai a ${commessaLink.numero_commessa}`}
            >
              <ExternalLink className="w-4 h-4" />
            </button>
          )}

          {/* Upload nascosto (fallback) */}
          <input ref={uploadRef} type="file" accept=".docx" className="hidden" onChange={handleUploadDocumento} />

          {/* Separatore */}
          <div className="h-6 w-px bg-gray-200" />

          {/* GENERA STAMPA — prominente */}
          {offerta.has_documento ? (
            <div className="flex flex-col gap-2">
            <div className="flex items-center gap-1">
              <button
                onClick={openOnlyofficeEditor}
                className="p-2 bg-blue-600 text-white rounded-l-lg hover:bg-blue-700"
                title="Apri documento in ONLYOFFICE"
              >
                <Eye className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={async () => {
                  if (agentAvailable && offerta) {
                    const filename = offerta.documento_filename || `offerta_${offerta.numero_offerta}.docx`
                    const ok = await openWithAgent(offerta.id, 'offerta', filename)
                    if (ok) {
                      setToast({ message: 'Documento inviato a MaestroAgent — si aprirà in Word', type: 'success' })
                    } else {
                      setToast({ message: 'Errore agent, uso metodo alternativo...', type: 'warning' })
                      handleWordEdit()
                    }
                  } else {
                    handleWordEdit()
                  }
                }}
                disabled={wordSaving}
                className={`flex items-center gap-1 px-2 py-1.5 text-white text-sm disabled:opacity-50 ${agentAvailable ? 'bg-green-600 hover:bg-green-700' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                title={agentAvailable ? 'Apri con Word (Desktop Agent)' : 'Modifica in Word — salva, modifica, ricarica con un click'}
              >
                {wordSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
              </button>
              <button
                onClick={handleVerificaDocumento}
                className="px-2 py-1.5 bg-blue-400 text-white hover:bg-blue-500"
                title="Verifica documento vs dati offerta"
              >
                <Search className="w-3.5 h-3.5" />
              </button>
              <div className="relative">
                <button
                  onClick={(e) => { e.stopPropagation(); setShowTemplateMenu(!showTemplateMenu) }}
                  disabled={generatingDoc}
                  className="flex items-center gap-1 px-2 py-1.5 bg-blue-400 text-white rounded-r-lg hover:bg-blue-500 disabled:opacity-50"
                  title="Rigenera documento / Scarica PDF"
                >
                  {generatingDoc ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><History className="w-3.5 h-3.5" /><ChevronDown className="w-3 h-3" /></>}
                </button>
                {showTemplateMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-10">
                    <button
                      onClick={handleDownloadPdf}
                      className="w-full flex items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <FileDown className="w-4 h-4 text-red-500" />
                      PDF Standard
                    </button>
                    {templates.length > 0 && (
                      <>
                        <div className="border-t border-gray-100 my-1" />
                        <div className="px-4 py-1 text-xs text-gray-400 uppercase">Template Word</div>
                        {templates.map((t) => (
                          <button
                            key={t.id}
                            onClick={() => handleGeneraDocumento(t.id)}
                            className="w-full flex items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                          >
                            <FileText className="w-4 h-4 text-blue-500" />
                            {t.nome}
                            {t.is_default && (
                              <span className="ml-auto text-xs text-primary-600">(Default)</span>
                            )}
                          </button>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Banner Word Edit Mode */}
            {wordEditMode && (
              <div className="flex items-center gap-3 px-4 py-2.5 bg-indigo-50 border border-indigo-200 rounded-lg">
                <FileText className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                <span className="text-sm text-indigo-800 flex-1">
                  Stai modificando in Word. Salva il file (Ctrl+S), poi clicca per caricare.
                </span>
                <button
                  onClick={handleWordUpload}
                  disabled={wordUploading}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50"
                >
                  {wordUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {wordUploading ? 'Caricamento...' : 'Carica Modifiche'}
                </button>
                <button
                  onClick={handleWordCancel}
                  className="p-1.5 text-indigo-400 hover:text-indigo-600 rounded"
                  title="Annulla modifica Word"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            </div>
          ) : (
            <div className="relative">
              <button
                onClick={(e) => { e.stopPropagation(); setShowTemplateMenu(!showTemplateMenu) }}
                disabled={generatingDoc}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm"
                title="Genera documento offerta da template"
              >
                {generatingDoc ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Printer className="w-3.5 h-3.5" />}
                Genera Stampa
                <ChevronDown className="w-3 h-3" />
              </button>
              {showTemplateMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-10">
                  <button
                    onClick={handleDownloadPdf}
                    className="w-full flex items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <FileDown className="w-4 h-4 text-red-500" />
                    PDF Standard
                  </button>
                  {templates.length > 0 && (
                    <>
                      <div className="border-t border-gray-100 my-1" />
                      <div className="px-4 py-1 text-xs text-gray-400 uppercase">Template Word</div>
                      {templates.map((t) => (
                        <button
                          key={t.id}
                          onClick={() => handleGeneraDocumento(t.id)}
                          className="w-full flex items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                        >
                          <FileText className="w-4 h-4 text-blue-500" />
                          {t.nome}
                          {t.is_default && (
                            <span className="ml-auto text-xs text-primary-600">(Default)</span>
                          )}
                        </button>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* GENERA CONTRATTO — prominente, solo per offerte standard */}
          {offerta.tipo_offerta !== 'integrativa' && (
            <>
              {contrattoInfo?.has_contratto ? (
                <div className="flex items-center gap-0.5">
                  <button
                    onClick={openContrattoEditor}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white rounded-l-lg hover:bg-purple-700 text-sm"
                    title="Apri contratto in ONLYOFFICE"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Contratto
                  </button>
                  <button
                    onClick={handleDownloadContratto}
                    className="px-2 py-1.5 bg-purple-500 text-white hover:bg-purple-600"
                    title="Download contratto"
                  >
                    <Download className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={handleGeneraContratto}
                    disabled={generandoContratto}
                    className="flex items-center gap-1 px-2 py-1.5 bg-purple-400 text-white rounded-r-lg hover:bg-purple-500 disabled:opacity-50"
                    title="Rigenera contratto con AI"
                  >
                    {generandoContratto ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <History className="w-3.5 h-3.5" />}
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleGeneraContratto}
                  disabled={generandoContratto}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm"
                  title="Genera contratto con AI"
                >
                  {generandoContratto ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      Generazione...
                    </>
                  ) : (
                    <>
                      <FileText className="w-3.5 h-3.5" />
                      Genera Contratto
                    </>
                  )}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Info Header — solo titolo modificabile */}
      {isEditable && (
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Titolo offerta</label>
          <input
            type="text"
            value={offerta.titolo}
            onChange={(e) => handleUpdate({ titolo: e.target.value })}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      )}

      {/* Statistiche offerte cliente */}
      {offerta.cliente?.id && (
        <div className="mb-6">
          <ClienteStatsCard clienteId={offerta.cliente.id} />
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Colonna Sinistra - Costi */}
        <CostiSection
          costiDiretti={offerta.costi_diretti}
          costiCalcolati={offerta.costi_calcolati}
          onAddCosto={handleAddCosto}
          onAddCostoMagazzino={handleAddCostoMagazzino}
          onDeleteCosto={handleDeleteCosto}
          disabled={!isEditable}
          integrativaLabels={offerta.integrativa_labels || {}}
        />

        {/* Colonna Destra - Valori */}
        <ValoriSection
          voci={offerta.voci}
          listino={listino}
          totaleValore={offerta.totale_valore}
          subtotaleVoci={offerta.subtotale_voci}
          scontoFinalePercentuale={offerta.sconto_finale_percentuale}
          onAddVoce={handleAddVoce}
          onAddNota={handleAddNota}
          onAddVoceLibera={handleAddVoceLibera}
          onUpdateVoce={handleUpdateVoce}
          onUpdateNota={handleUpdateNota}
          onDeleteVoce={handleDeleteVoce}
          onReorder={handleReorderVoci}
          onScontoFinaleChange={handleScontoFinaleChange}
          disabled={!isEditable}
          integrativaLabels={offerta.integrativa_labels || {}}
        />
      </div>

      {/* Canoni Ricorsivi — solo per offerte standard */}
      {offerta.tipo_offerta !== 'integrativa' && (
      <div className="mt-6">
        <CanoniSection
          canoni={offerta.canoni_ricorsivi || []}
          listino={listino}
          totaleCanoni={offerta.totale_canoni_ricorsivi || 0}
          onAddCanone={handleAddCanone}
          onAddNota={handleAddCanoneNota}
          onAddCanoneLibero={handleAddCanoneLibero}
          onUpdateCanone={handleUpdateCanone}
          onUpdateNota={handleUpdateCanoneNota}
          onDeleteCanone={handleDeleteCanone}
          onReorder={handleReorderCanoni}
          disabled={!isEditable}
        />
      </div>
      )}

      {/* Scaglioni di Pagamento — solo per offerte standard */}
      {offerta.tipo_offerta !== 'integrativa' && (
      <div className="mt-6 bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Scaglioni di Pagamento</h3>
          {isEditable && (
            <button
              onClick={() => setShowScaglioneForm(!showScaglioneForm)}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <Plus className="w-4 h-4" />
              Aggiungi
            </button>
          )}
        </div>

        {/* Barra progresso */}
        {offerta.totale_valore > 0 && offerta.scaglioni.length > 0 && (() => {
          const percentualeCoperta = (offerta.totale_scaglioni / offerta.totale_valore) * 100
          const isOver = percentualeCoperta > 100
          const isComplete = Math.abs(percentualeCoperta - 100) < 0.01
          return (
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">
                  Coperto: {formatCurrency(offerta.totale_scaglioni)} / {formatCurrency(offerta.totale_valore)}
                </span>
                <span className={`font-medium ${isOver ? 'text-red-600' : isComplete ? 'text-green-600' : 'text-amber-600'}`}>
                  {percentualeCoperta.toFixed(1)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className={`h-2.5 rounded-full transition-all ${isOver ? 'bg-red-500' : isComplete ? 'bg-green-500' : 'bg-amber-500'}`}
                  style={{ width: `${Math.min(percentualeCoperta, 100)}%` }}
                />
              </div>
              {!isComplete && !isOver && (
                <p className="text-xs text-amber-600 mt-1">
                  Mancano {formatCurrency(offerta.totale_valore - offerta.totale_scaglioni)} per coprire il totale offerta
                </p>
              )}
            </div>
          )
        })()}

        {/* Lista scaglioni */}
        {offerta.scaglioni.length === 0 ? (
          <p className="text-sm text-gray-400 py-3">Nessuno scaglione di pagamento definito</p>
        ) : (
          <div className="space-y-2 mb-4">
            {offerta.scaglioni.map((sc) => (
              <div key={sc.id} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-900">{sc.descrizione}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    sc.tipo === 'percentuale'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-green-100 text-green-700'
                  }`}>
                    {sc.tipo === 'percentuale' ? `${sc.percentuale}%` : 'Fisso'}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-gray-900">
                    {formatCurrency(sc.importo_calcolato)}
                  </span>
                  {isEditable && (
                    <button
                      onClick={() => handleDeleteScaglione(sc.id)}
                      className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Form aggiunta */}
        {showScaglioneForm && isEditable && (
          <div className="border border-gray-200 rounded-lg p-4 space-y-3">
            <input
              type="text"
              placeholder="Descrizione (es. Anticipo, Primo SAL, Saldo)"
              value={scaglioneForm.descrizione}
              onChange={(e) => setScaglioneForm({ ...scaglioneForm, descrizione: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
            />
            <div className="grid grid-cols-3 gap-3">
              <select
                value={scaglioneForm.tipo}
                onChange={(e) => setScaglioneForm({
                  ...scaglioneForm,
                  tipo: e.target.value as TipoScaglione,
                  percentuale: undefined,
                  importo: undefined,
                })}
                className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white text-sm"
              >
                <option value="percentuale">Percentuale (%)</option>
                <option value="importo">Importo fisso</option>
              </select>
              {scaglioneForm.tipo === 'percentuale' ? (
                <input
                  type="number"
                  placeholder="% del totale"
                  min="0.01"
                  max="100"
                  step="0.5"
                  value={scaglioneForm.percentuale ?? ''}
                  onChange={(e) => setScaglioneForm({ ...scaglioneForm, percentuale: parseFloat(e.target.value) || undefined })}
                  className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                />
              ) : (
                <input
                  type="number"
                  placeholder="Importo"
                  min="0.01"
                  step="0.01"
                  value={scaglioneForm.importo ?? ''}
                  onChange={(e) => setScaglioneForm({ ...scaglioneForm, importo: parseFloat(e.target.value) || undefined })}
                  className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                />
              )}
              <button
                onClick={handleAddScaglione}
                disabled={!scaglioneForm.descrizione || (scaglioneForm.tipo === 'percentuale' ? !scaglioneForm.percentuale : !scaglioneForm.importo)}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm"
              >
                Aggiungi
              </button>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Riepilogo — solo per offerte standard con integrative */}
      {offerta.tipo_offerta !== 'integrativa' && offerta.integrative.length > 0 && (
      <div className="mt-6 bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Riepilogo</h3>
          {offerta.stato === 'confermata' && (
            <button
              onClick={() => setShowIntegrativaModal(true)}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700"
            >
              <Plus className="w-4 h-4" />
              Nuova Integrativa
            </button>
          )}
        </div>

        {/* Tabella riepilogo */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 pr-4 font-medium text-gray-500">Documento</th>
                <th className="text-left py-2 px-2 font-medium text-gray-500">Stato</th>
                <th className="text-right py-2 px-2 font-medium text-gray-500">Valore</th>
                <th className="text-right py-2 px-2 font-medium text-gray-500">Costi</th>
                <th className="text-right py-2 pl-2 font-medium text-gray-500">GG</th>
                <th className="py-2 w-8"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {/* Riga offerta principale */}
              <tr className="font-medium">
                <td className="py-2.5 pr-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xs px-2 py-0.5 rounded bg-primary-100 text-primary-700 font-bold">PRINC.</span>
                    <span className="font-mono text-xs text-gray-500">{offerta.numero_offerta}</span>
                  </div>
                </td>
                <td className="py-2.5 px-2">
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">confermata</span>
                </td>
                <td className="py-2.5 px-2 text-right text-gray-900">{formatCurrency(offerta.totale_valore_consolidato - offerta.integrative.filter(i => i.stato === 'confermata').reduce((s, i) => s + i.totale_valore, 0))}</td>
                <td className="py-2.5 px-2 text-right text-gray-900">{formatCurrency(offerta.totale_costi_consolidato - offerta.integrative.filter(i => i.stato === 'confermata').reduce((s, i) => s + i.totale_costi, 0))}</td>
                <td className="py-2.5 pl-2 text-right text-gray-900">{offerta.giorni_personale_consolidato - offerta.integrative.filter(i => i.stato === 'confermata').reduce((s, i) => s + i.giorni_personale, 0)}</td>
                <td></td>
              </tr>
              {/* Righe integrative */}
              {offerta.integrative.map((integ) => {
                const statoColors: Record<string, string> = {
                  bozza: 'bg-gray-100 text-gray-700',
                  in_corso: 'bg-gray-100 text-gray-700',
                  in_revisione: 'bg-purple-100 text-purple-700',
                  approvata: 'bg-blue-100 text-blue-700',
                  in_attesa: 'bg-blue-100 text-blue-700',
                  confermata: 'bg-green-100 text-green-700',
                  rifiutata: 'bg-red-100 text-red-700',
                }
                return (
                  <tr
                    key={integ.id}
                    onClick={() => navigate(`/app/offerte/${integ.id}`)}
                    className="cursor-pointer hover:bg-orange-50 transition-colors"
                  >
                    <td className="py-2.5 pr-4">
                      <div className="flex items-center gap-2">
                        <span className="text-xs px-2 py-0.5 rounded bg-orange-100 text-orange-700 font-bold">INT #{integ.numero_integrativa}</span>
                        <span className="text-gray-700">{integ.titolo}</span>
                      </div>
                    </td>
                    <td className="py-2.5 px-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statoColors[integ.stato] || 'bg-gray-100 text-gray-700'}`}>
                        {integ.stato}
                      </span>
                    </td>
                    <td className="py-2.5 px-2 text-right text-gray-900">{formatCurrency(integ.totale_valore)}</td>
                    <td className="py-2.5 px-2 text-right text-gray-900">{formatCurrency(integ.totale_costi)}</td>
                    <td className="py-2.5 pl-2 text-right text-gray-900">{integ.giorni_personale}</td>
                    <td className="py-2.5 text-right"><ExternalLink className="w-3.5 h-3.5 text-gray-400" /></td>
                  </tr>
                )
              })}
            </tbody>
            {/* Riga totale */}
            <tfoot>
              <tr className="border-t-2 border-gray-300 font-bold">
                <td className="py-3 pr-4 text-gray-900">TOTALE CONSOLIDATO</td>
                <td></td>
                <td className="py-3 px-2 text-right text-orange-700 text-base">{formatCurrency(offerta.totale_valore_consolidato)}</td>
                <td className="py-3 px-2 text-right text-orange-700 text-base">{formatCurrency(offerta.totale_costi_consolidato)}</td>
                <td className="py-3 pl-2 text-right text-orange-700">{offerta.giorni_personale_consolidato}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      )}

      {/* Grafico Profitto */}
      <div className="mt-6 bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Analisi Profitto</h3>
        {(offerta.totale_canoni_ricorsivi || 0) > 0 && (
          <div className="mb-4 p-3 bg-indigo-50 rounded-lg border border-indigo-100">
            <div className="flex items-center justify-between text-sm">
              <span className="text-indigo-700 font-medium">Canoni Ricorsivi (separati)</span>
              <span className="text-indigo-700 font-bold">{formatCurrency(offerta.totale_canoni_ricorsivi)}</span>
            </div>
          </div>
        )}
        <ProfitChart
          costiDiretti={offerta.costi_calcolati.costi_diretti_totale}
          costoPersonale={offerta.costi_calcolati.costo_personale_totale}
          costoMezzi={offerta.costi_calcolati.costo_mezzi_totale}
          totaleValore={offerta.totale_valore}
          margineEffettivo={offerta.margine_percentuale}
          margineDesiderato={offerta.margine_desiderato}
        />
      </div>

      {/* Foto Offerta */}
      <div className="mt-6">
        <FotoSection
          offertaId={offerta.id}
          foto={foto}
          onFotoChange={handleFotoChange}
          disabled={!isEditable}
        />
      </div>

      {/* Note */}
      {isEditable && (
        <div className="mt-6 bg-white rounded-xl shadow-sm p-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Note</label>
          <textarea
            rows={3}
            value={offerta.note || ''}
            onChange={(e) => handleUpdate({ note: e.target.value })}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
            placeholder="Note aggiuntive per l'offerta..."
          />
        </div>
      )}

      {/* Revisioni (collapsible) */}
      {showRevisioni && (
        <div className="mt-6 bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <History className="w-5 h-5 text-purple-600" />
              Revisioni ({revisioni.length})
            </h3>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={revisioneNote}
                onChange={(e) => setRevisioneNote(e.target.value)}
                placeholder="Note revisione..."
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <button
                onClick={handleCreaRevisione}
                disabled={creatingRevisione}
                className="flex items-center gap-1 px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm disabled:opacity-50"
              >
                {creatingRevisione ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                Crea Revisione
              </button>
            </div>
          </div>
          {revisioni.length === 0 ? (
            <p className="text-sm text-gray-500">Nessuna revisione ancora creata</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 px-3 text-gray-600">Rev.</th>
                    <th className="text-left py-2 px-3 text-gray-600">Data</th>
                    <th className="text-left py-2 px-3 text-gray-600">Note</th>
                    <th className="text-right py-2 px-3 text-gray-600">Totale Valore</th>
                    <th className="text-right py-2 px-3 text-gray-600">Totale Costi</th>
                    <th className="text-center py-2 px-3 text-gray-600">Doc</th>
                  </tr>
                </thead>
                <tbody>
                  {revisioni.map((rev) => (
                    <tr key={rev.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-2 px-3 font-medium text-purple-700">#{rev.numero_revisione}</td>
                      <td className="py-2 px-3 text-gray-600">
                        {rev.data_revisione ? new Date(rev.data_revisione).toLocaleString('it-IT') : '-'}
                      </td>
                      <td className="py-2 px-3 text-gray-600">{rev.note_revisione || '-'}</td>
                      <td className="py-2 px-3 text-right">
                        {rev.snapshot_totale_valore != null ? `€ ${Number(rev.snapshot_totale_valore).toLocaleString('it-IT', { minimumFractionDigits: 2 })}` : '-'}
                      </td>
                      <td className="py-2 px-3 text-right">
                        {rev.snapshot_totale_costi != null ? `€ ${Number(rev.snapshot_totale_costi).toLocaleString('it-IT', { minimumFractionDigits: 2 })}` : '-'}
                      </td>
                      <td className="py-2 px-3 text-center">
                        {rev.has_documento ? (
                          <button
                            onClick={() => handleDownloadRevisioneDoc(rev.id)}
                            className="text-blue-600 hover:text-blue-800"
                            title="Scarica documento"
                          >
                            <Download className="w-4 h-4 inline" />
                          </button>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modale Nuova Integrativa */}
      {showIntegrativaModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Nuova Offerta Integrativa</h2>
            <p className="text-sm text-gray-500 mb-4">
              Crea un'integrazione per <strong>{offerta.numero_offerta}</strong>.
              Le anagrafiche e l'indirizzo saranno ereditati dall'offerta principale.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titolo *</label>
                <input
                  type="text"
                  value={integrativaForm.titolo}
                  onChange={e => setIntegrativaForm({ ...integrativaForm, titolo: e.target.value })}
                  placeholder="Es. Aggiunta postazione, Rimozione linea..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione</label>
                <textarea
                  rows={3}
                  value={integrativaForm.descrizione}
                  onChange={e => setIntegrativaForm({ ...integrativaForm, descrizione: e.target.value })}
                  placeholder="Dettagli dell'integrazione..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => { setShowIntegrativaModal(false); setIntegrativaForm({ titolo: '', descrizione: '' }) }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Annulla
              </button>
              <button
                onClick={handleCreateIntegrativa}
                disabled={creatingIntegrativa || !integrativaForm.titolo.trim()}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
              >
                {creatingIntegrativa ? 'Creazione...' : 'Crea Integrativa'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ONLYOFFICE Editor Modal (fullscreen overlay) */}
      {showOnlyoffice && (
        <div className="fixed inset-0 z-50 bg-white flex flex-col">
          <div className="flex items-center justify-between px-4 py-2 bg-gray-100 border-b border-gray-300">
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-blue-600" />
              <span className="font-medium text-gray-800">
                {offerta.documento_filename || 'Documento Offerta'} — {offerta.numero_offerta}
                {(offerta.numero_revisione || 0) > 0 && ` Rev.${offerta.numero_revisione}`}
              </span>
            </div>
            <button
              onClick={closeOnlyofficeEditor}
              className="flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
            >
              <X className="w-4 h-4" />
              Chiudi Editor
            </button>
          </div>
          <div className="flex-1 relative">
            {onlyofficeLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                <span className="ml-2 text-gray-600">Caricamento editor...</span>
              </div>
            )}
            <div
              id="onlyoffice-offerta-container"
              ref={onlyofficeRef}
              style={{ height: 'calc(100vh - 48px)', width: '100%' }}
            />
          </div>
        </div>
      )}

      {/* ONLYOFFICE Editor Contratto - Fullscreen Overlay */}
      {showContrattoEditor && (
        <div className="fixed inset-0 z-50 bg-white flex flex-col">
          <div className="flex items-center justify-between px-4 py-2 bg-purple-50 border-b border-purple-200">
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-purple-600" />
              <span className="font-medium text-gray-800">
                Contratto — {offerta?.numero_offerta}
              </span>
            </div>
            <button
              onClick={closeContrattoEditor}
              className="flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
            >
              <X className="w-4 h-4" />
              Chiudi Editor
            </button>
          </div>
          <div className="flex-1 relative">
            {contrattoEditorLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
                <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                <span className="ml-2 text-gray-600">Caricamento editor contratto...</span>
              </div>
            )}
            <div
              id="onlyoffice-contratto-container"
              ref={contrattoEditorRef}
              style={{ height: 'calc(100vh - 48px)', width: '100%' }}
            />
          </div>
        </div>
      )}

      {/* Modale Generazione Contratto AI */}
      {generandoContratto && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-8 text-center">
            <div className="mx-auto w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
              <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Generazione Contratto in corso</h3>
            <p className="text-gray-600 text-sm mb-1">
              L'intelligenza artificiale sta analizzando i dati dell'offerta e redigendo il contratto.
            </p>
            <p className="text-gray-400 text-xs">
              L'operazione potrebbe richiedere fino a un minuto.
            </p>
          </div>
        </div>
      )}

      {/* Verifica Documento Modal */}
      {showVerifica && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Search className="w-5 h-5 text-orange-600" />
                Verifica Documento
              </h3>
              <button onClick={() => { setShowVerifica(false); setVerificaResult(null) }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              {verificaLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                  <span className="ml-2 text-gray-600">Analisi in corso...</span>
                </div>
              ) : verificaResult ? (
                <>
                  {verificaResult.esito === 'ok' ? (
                    <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                      <span className="text-green-800 font-medium">Nessuna discrepanza rilevata. Il documento corrisponde ai dati dell'offerta.</span>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center gap-3 p-4 bg-orange-50 rounded-lg border border-orange-200 mb-4">
                        <AlertTriangle className="w-6 h-6 text-orange-600" />
                        <div>
                          <span className="text-orange-800 font-medium">
                            Trovate {verificaResult.totale_discrepanze} discrepanze
                          </span>
                          {verificaResult.critiche > 0 && (
                            <span className="ml-2 text-red-600 text-sm">({verificaResult.critiche} critiche)</span>
                          )}
                        </div>
                      </div>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-2 px-3 text-gray-600">Severita</th>
                            <th className="text-left py-2 px-3 text-gray-600">Campo</th>
                            <th className="text-left py-2 px-3 text-gray-600">Descrizione</th>
                          </tr>
                        </thead>
                        <tbody>
                          {verificaResult.discrepanze.map((d, i) => (
                            <tr key={i} className="border-b border-gray-100">
                              <td className="py-2 px-3">
                                <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                                  d.severita === 'critico' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {d.severita}
                                </span>
                              </td>
                              <td className="py-2 px-3 font-mono text-xs text-gray-600">{d.campo}</td>
                              <td className="py-2 px-3 text-gray-700">{d.descrizione}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Modale Invio Forzato */}
      {showInvioForzato && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              <h4 className="text-lg font-semibold text-gray-900">Scorte insufficienti</h4>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Uno o piu' articoli non hanno scorte sufficienti a magazzino. Per inviare comunque l'offerta, inserisci la tua password.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                type="password"
                value={invioPassword}
                onChange={(e) => setInvioPassword(e.target.value)}
                placeholder="Inserisci password..."
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                onKeyDown={(e) => e.key === 'Enter' && handleInvioForzato()}
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setShowInvioForzato(false); setInvioPassword('') }}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
              >Annulla</button>
              <button
                onClick={handleInvioForzato}
                disabled={!invioPassword || invioLoading}
                className="px-4 py-2 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
              >{invioLoading ? 'Verifica...' : 'Invia con Forzatura'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
